import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Heart, ArrowRight, Shield, FileText, MessagesSquare, Sparkles } from 'lucide-react';
import PrivacyBadge from '@/components/lite/PrivacyBadge';

export default function LiteEntry() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#FBFAF7] flex flex-col">
      {/* Top bar */}
      <header className="px-4 sm:px-6 py-4 flex items-center justify-between border-b border-stone-100 bg-white">
        <div className="flex items-center gap-2">
          <Heart className="w-5 h-5 text-primary" />
          <span className="font-serif text-lg text-primary-900">PlayFirst Lite</span>
          <span className="hidden sm:inline text-xs text-stone-500 ml-1">
            · Private AI workspace for SLPs
          </span>
        </div>
        <div className="flex items-center gap-3 text-sm">
          <button
            onClick={() => navigate('/')}
            className="text-stone-500 hover:text-primary"
            data-testid="lite-entry-international-link"
          >
            Full PlayFirst (intl)
          </button>
        </div>
      </header>

      {/* Hero */}
      <main className="flex-1 flex items-center">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12 sm:py-20 w-full">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="space-y-8"
          >
            <div className="inline-flex items-center gap-2 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1">
              <Shield className="w-3.5 h-3.5" />
              <span>Stateless · No accounts · No data stored</span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif text-primary-900 leading-tight tracking-tight">
              Write clinical docs in minutes—<br className="hidden sm:block" />
              without storing client data.
            </h1>

            <p className="text-lg text-stone-600 max-w-2xl leading-relaxed">
              No account required. De-identified inputs only. Same PlayFirst-quality outputs.
              Built for US SLPs who need fast, neurodiversity-affirming clinical writing without
              the compliance headache.
            </p>

            <div className="flex flex-wrap items-center gap-3">
              <Button
                size="lg"
                onClick={() => navigate('/us/tools')}
                className="rounded-full text-base px-8"
                data-testid="lite-start-btn"
              >
                Start
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <a
                href="#how-private"
                className="text-sm text-stone-600 hover:text-primary underline-offset-4 hover:underline"
                data-testid="lite-howprivate-btn"
              >
                How it stays private
              </a>
            </div>

            <div className="pt-4">
              <PrivacyBadge />
            </div>
          </motion.div>

          {/* What you can do */}
          <div className="mt-16 grid sm:grid-cols-3 gap-4" id="how-private">
            <FeatureCard
              icon={<FileText className="w-5 h-5" />}
              title="Clinical Writing"
              body="Reports, parent summaries, referral letters."
            />
            <FeatureCard
              icon={<Sparkles className="w-5 h-5" />}
              title="Planning"
              body="IEP drafts, sessions and activities (V1.5 coming)."
            />
            <FeatureCard
              icon={<MessagesSquare className="w-5 h-5" />}
              title="GLP Specialist"
              body="GLP stage analysis with cautious clinical framing."
            />
          </div>

          {/* Privacy explainer */}
          <div className="mt-16 rounded-2xl border border-stone-200 bg-white p-6 sm:p-8">
            <h2 className="text-xl font-serif text-primary-900 mb-3">How it stays private</h2>
            <ul className="space-y-2 text-stone-700 text-sm">
              <li>• Your inputs are scanned for identifiers (names, DOBs, addresses, phone, email, schools) before AI processing.</li>
              <li>• We do not store any of your inputs or outputs. Only anonymized usage counts (hashed IP, tool name, timestamp) are recorded for rate limiting.</li>
              <li>• Optional email is used only to lift the daily generation limit, and (if you opt in) to notify you when an upgraded US tier launches with full BAA coverage.</li>
              <li>• AI requests are sent over encrypted connections to OpenAI on standard tier (30-day retention by OpenAI). Because all inputs are de-identified per HIPAA Safe Harbor, this is not PHI.</li>
              <li>• You are responsible for confirming inputs are de-identified before submission.</li>
            </ul>
          </div>
        </div>
      </main>

      <footer className="border-t border-stone-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 flex flex-wrap items-center justify-between gap-3">
          <PrivacyBadge compact />
          <div className="text-xs text-stone-400">
            AI-generated drafts. Clinician judgment required.
          </div>
        </div>
      </footer>
    </div>
  );
}

function FeatureCard({ icon, title, body }) {
  return (
    <div className="rounded-2xl border border-stone-200 bg-white p-5">
      <div className="w-9 h-9 rounded-full bg-primary/10 text-primary flex items-center justify-center mb-3">
        {icon}
      </div>
      <h3 className="font-medium text-primary-900 mb-1">{title}</h3>
      <p className="text-sm text-stone-500">{body}</p>
    </div>
  );
}
